/**
 * Decide whether this signed-in account still needs push setup on this exact
 * device. Browser permission and a local consent marker are not enough. The
 * current browser token must also still be present on the account document.
 */
export function needsPushRecovery(
  status: string | undefined,
  appPreference: boolean | undefined,
  storedTokens: string[] | undefined,
  currentToken: string | null,
  permission: NotificationPermission | 'unsupported' = 'granted',
  enrolledHere = true
): boolean {
  // Missing appPreference is the legacy default-on state. Only an explicit
  // false is a choice to stop phone alerts and suppress the setup prompt.
  if (status !== 'active' || appPreference === false) return false;
  if (permission !== 'granted' || !enrolledHere) return true;
  return !currentToken || !(storedTokens ?? []).includes(currentToken);
}

export type PushDeviceGuidance =
  | 'ready'
  | 'legacy-ios'
  | 'ios-needs-home-screen'
  | 'unsupported';

export interface PushDeviceSignals {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  standalone: boolean;
  pushApiAvailable: boolean;
}

/**
 * Pick the useful next step for this device.
 *
 * Apple added Web Push in iOS and iPadOS 16.4, and supports it only for a web
 * app opened from its Home Screen icon. Version parsing is used only for the
 * clear pre-16.4 message. Capability checks remain authoritative elsewhere.
 */
export function pushDeviceGuidance(signals: PushDeviceSignals): PushDeviceGuidance {
  const appleMobileUa = /\b(iPhone|iPad|iPod)\b/i.test(signals.userAgent);
  // iPadOS can identify itself as a touch-enabled Mac in desktop mode.
  const appleMobile = appleMobileUa
    || (signals.platform === 'MacIntel' && signals.maxTouchPoints > 1);
  const versionMatch = signals.userAgent.match(
    /(?:CPU(?: iPhone)? OS|iPhone OS)\s+(\d+)[._](\d+)/i
  );

  if (appleMobile && versionMatch) {
    const major = Number(versionMatch[1]);
    const minor = Number(versionMatch[2]);
    if (major < 16 || (major === 16 && minor < 4)) return 'legacy-ios';
  }
  // iPadOS desktop mode identifies as a touch-enabled Mac and does not expose
  // a reliable OS version in the user agent. In a Home Screen app, the absent
  // Push API is enough to give upgrade guidance instead of hiding the gate.
  if (appleMobile && signals.standalone && !signals.pushApiAvailable) return 'legacy-ios';
  if (appleMobile && !signals.standalone) return 'ios-needs-home-screen';
  if (!signals.pushApiAvailable) return 'unsupported';
  return 'ready';
}
